// Client Portal — RAG Audit Log panel. Extracted from App.js.
import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Search, ShieldCheck } from "lucide-react";
import { authFetch } from "../lib/api";
import { loadAuditLog } from "../lib/ragConfig";

export const RAGAuditLog = ({ facility }) => {
  const fid = facility.facility_id;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterAction, setFilterAction] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [range, setRange] = useState("7d");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authFetch(
        `/rag/audit?facility_id=${encodeURIComponent(fid)}&limit=1000`,
      );
      if (Array.isArray(data)) {
        setEvents(
          data.map((e) => ({
            id: e.id,
            action: e.action,
            user: e.performed_by_email || e.performed_by_id || "—",
            timestamp: e.created_at,
            target_id: e.target_id,
            target_type: e.target_type,
            ...(e.details || {}),
          })),
        );
      } else {
        // fallback to localStorage if backend unavailable
        setEvents(loadAuditLog(fid));
      }
    } catch {
      setEvents(loadAuditLog(fid));
    }
    setLoading(false);
  }, [fid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const now = new Date();
  const rangeMs = {
    "24h": 86400000,
    "7d": 604800000,
    "30d": 2592000000,
    all: Infinity,
  }[range];
  const cutoff = new Date(now - rangeMs);

  const actionLabels = {
    api_key_created: {
      label: "API Key Created",
      color: "bg-blue-100 text-blue-700",
    },
    api_key_revoked: {
      label: "API Key Revoked",
      color: "bg-amber-100 text-amber-700",
    },
    api_key_deleted: {
      label: "API Key Deleted",
      color: "bg-red-100 text-red-700",
    },
    document_uploaded: {
      label: "Document Uploaded",
      color: "bg-green-100 text-green-700",
    },
    document_approved: {
      label: "Document Approved",
      color: "bg-green-100 text-green-700",
    },
    document_denied: {
      label: "Document Denied",
      color: "bg-red-100 text-red-700",
    },
    user_invited: { label: "User Invited", color: "bg-blue-100 text-blue-700" },
    user_role_changed: {
      label: "Role Changed",
      color: "bg-purple-100 text-purple-700",
    },
    user_toggled: {
      label: "User Toggled",
      color: "bg-amber-100 text-amber-700",
    },
    config_saved: {
      label: "Config Saved",
      color: "bg-slate-100 text-slate-700",
    },
    prompt_config_saved: {
      label: "Prompt Config Saved",
      color: "bg-indigo-100 text-indigo-700",
    },
    login: { label: "Login", color: "bg-green-100 text-green-700" },
    query: { label: "Query", color: "bg-blue-100 text-blue-700" },
    error: { label: "Error", color: "bg-red-100 text-red-700" },
  };
  const defaultAction = {
    label: "Action",
    color: "bg-slate-100 text-slate-700",
  };

  const uniqueActions = [...new Set(events.map((e) => e.action))];
  const uniqueUsers = [...new Set(events.map((e) => e.user).filter(Boolean))];

  const filtered = events
    .filter((e) => new Date(e.timestamp) >= cutoff)
    .filter((e) => filterAction === "all" || e.action === filterAction)
    .filter((e) => filterUser === "all" || e.user === filterUser)
    .filter(
      (e) =>
        !searchTerm ||
        (e.action || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.user || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        JSON.stringify(e).toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Audit Log</h3>
          <p className="text-sm text-slate-500">
            Activity history for {facility.facility_name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex space-x-1 bg-white rounded-lg border border-slate-200 p-1">
            {[
              { id: "24h", label: "24h" },
              { id: "7d", label: "7d" },
              { id: "30d", label: "30d" },
              { id: "all", label: "All" },
            ].map((r) => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  range === r.id
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 py-1.5 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 mr-2" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search events..."
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700"
        >
          <option value="all">All Actions</option>
          {uniqueActions.map((a) => (
            <option key={a} value={a}>
              {(actionLabels[a] || defaultAction).label}
            </option>
          ))}
        </select>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700"
        >
          <option value="all">All Users</option>
          {uniqueUsers.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm text-center">
          <p className="text-2xl font-bold text-slate-900">{filtered.length}</p>
          <p className="text-xs text-slate-500">Events in period</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm text-center">
          <p className="text-2xl font-bold text-slate-900">
            {new Set(filtered.map((e) => e.user).filter(Boolean)).size}
          </p>
          <p className="text-xs text-slate-500">Active users</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm text-center">
          <p className="text-2xl font-bold text-slate-900">
            {filtered.filter((e) => e.action === "error").length}
          </p>
          <p className="text-xs text-slate-500">Errors</p>
        </div>
      </div>

      {/* Event list */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="max-h-[500px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                No events match your filters.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    User
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.slice(0, 200).map((evt) => {
                  const meta = actionLabels[evt.action] || defaultAction;
                  const details = Object.entries(evt)
                    .filter(
                      ([k]) =>
                        !["id", "action", "user", "timestamp", "type"].includes(
                          k,
                        ),
                    )
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ");
                  return (
                    <tr key={evt.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(evt.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-700">
                        {evt.user || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 max-w-[300px] truncate">
                        {details || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {filtered.length > 200 && (
          <div className="px-4 py-2 text-center text-xs text-slate-400 border-t border-slate-100">
            Showing first 200 of {filtered.length} events
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-start">
          <ShieldCheck className="w-4 h-4 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-xs text-green-700">
            Audit events are persisted server-side and visible to all admins
            across devices. All KB mutations (uploads, approvals, deletions,
            Q&amp;A pairs) are logged automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

// ── KB Gap Review Tasks ────────────────────────────────────────────
